/**
 * A pendência da composição quando a saída do pacote falhou (E209.F2.T09, GUS-296 item 8).
 *
 * Com a preparação da saída em `falhou`, a conferência da etapa `composicao` fica `corrigir` (a
 * prévia não se refez) e a prontidão diz "Corrigir a etapa que não liberou". O "Resolver" seguia o
 * passo e levava à aba Conclusão, onde não há ação; a recuperação é o "Retomar" da saída, no card
 * do pacote da aba Exame. Aqui a mesa junta as duas leituras que já faz (a prontidão e o pacote): a
 * pendência da composição passa a apontar a linha da saída no card e diz o motivo técnico da
 * falha. A régua da prontidão, as liberações e os portões não mudam.
 */
import { ptBR } from '@/i18n/pt-BR';
import type { PacoteDoCasoData, SaidaDoPacote } from '@/lib/preparacao_do_pacote';
import type { PendenciaDaProntidaoData } from '@/lib/resultadoAuditado';
import type { EstadoDoResultadoNaTela } from '@/lib/useResultadoDoCaso';

const tp = ptBR.preparacaoDoPacote;

/** A âncora da linha de uma saída no card do pacote (aba Exame). */
export function ancoraDaSaida(saida: string): string {
  return `pacote-${saida}`;
}

export interface SaidaQueFalhou {
  saida: SaidaDoPacote;
  /** A frase do motivo técnico, como o card do pacote a diz. */
  motivo: string;
}

/** A primeira saída do pacote em falha que se retoma (o exame antes do relatório de risco). */
export function saidaQueFalhou(pacote: PacoteDoCasoData | null): SaidaQueFalhou | null {
  const linha = pacote?.linhas.find(
    (l) => (l.chave === 'exame' || l.chave === 'riscos') && l.estado === 'falhou' && l.retomavel,
  );
  if (linha === undefined || (linha.chave !== 'exame' && linha.chave !== 'riscos')) return null;
  const motivos: Partial<Record<string, string>> = tp.motivo;
  const motivo =
    (linha.motivo != null ? motivos[linha.motivo] : undefined) ?? tp.motivo.interrompida;
  return { saida: linha.chave, motivo };
}

function daComposicao(p: PendenciaDaProntidaoData): boolean {
  return p.motivo === 'etapa_em_conferencia' && p.etapa === 'composicao';
}

/** A pendência da composição apontando a retomada da saída, com o motivo em `faltam`. */
export function comASaidaQueFalhou(
  pendencias: readonly PendenciaDaProntidaoData[],
  falha: SaidaQueFalhou | null,
): PendenciaDaProntidaoData[] {
  if (falha === null) return [...pendencias];
  return pendencias.map((p) =>
    daComposicao(p)
      ? {
          ...p,
          faltam: [falha.motivo],
          alvo: {
            aba: 'exame',
            ancora: ancoraDaSaida(falha.saida),
            documento_id: null,
            campo: null,
          },
        }
      : p,
  );
}

/** O estado do resultado que a aba Pendências lê, com a pendência da composição ajustada. */
export function resultadoComASaidaQueFalhou(
  resultado: EstadoDoResultadoNaTela,
  pacote: PacoteDoCasoData | null,
): EstadoDoResultadoNaTela {
  const falha = saidaQueFalhou(pacote);
  if (falha === null || resultado.estado !== 'pronto' || resultado.dados.prontidao === null) {
    return resultado;
  }
  const { prontidao } = resultado.dados;
  return {
    estado: 'pronto',
    dados: {
      ...resultado.dados,
      prontidao: { ...prontidao, pendencias: comASaidaQueFalhou(prontidao.pendencias, falha) },
    },
  };
}
